export type ThemePreference = "auto" | "light" | "dark";
export type MotionPreference = "auto" | "reduce";

export const DEFAULT_TEXT_SCALE = 1.0;
export const MIN_TEXT_SCALE = 0.75;
export const MAX_TEXT_SCALE = 1.5;

export interface UserSettings {
  theme: ThemePreference;
  motion: MotionPreference;
  textScale: number;
}

const STORAGE_KEY = "bk.chat-ui.settings.v1";

const DEFAULT_SETTINGS: UserSettings = {
  theme: "auto",
  motion: "auto",
  textScale: DEFAULT_TEXT_SCALE,
};

function parseSettings(raw: unknown): UserSettings {
  if (!raw || typeof raw !== "object") return DEFAULT_SETTINGS;
  const obj = raw as Record<string, unknown>;
  const theme: ThemePreference =
    obj.theme === "light" || obj.theme === "dark" || obj.theme === "auto" ? obj.theme : "auto";
  const motion: MotionPreference = obj.motion === "reduce" || obj.motion === "auto" ? obj.motion : "auto";
  const textScale =
    typeof obj.textScale === "number" && obj.textScale >= MIN_TEXT_SCALE && obj.textScale <= MAX_TEXT_SCALE
      ? obj.textScale
      : DEFAULT_TEXT_SCALE;
  return { theme, motion, textScale };
}

export function loadSettings(): UserSettings {
  if (typeof window === "undefined") return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return parseSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(s: UserSettings): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage may be unavailable in private/embedded contexts */
  }
}

export function applyThemeAttribute(theme: ThemePreference): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "auto") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
}

export function applyMotionAttribute(motion: MotionPreference): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (motion === "reduce") {
    root.setAttribute("data-motion", "reduce");
  } else {
    root.removeAttribute("data-motion");
  }
}

export function applyTextScale(scale: number): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (scale === DEFAULT_TEXT_SCALE) {
    root.style.removeProperty("--app-text-scale");
  } else {
    root.style.setProperty("--app-text-scale", String(scale));
  }
}
